//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  KeyboardEvent,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useUserAuth } from '../../UserAuth';
import LoopDataService from '../../utils/LoopDataService';
import { useLoopContext } from '../../LoopContext';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowDown, Close, Search } from '@mui/icons-material';
import { useSearchContext } from '../../components/SearchContext';

interface SearchBoxProps {
  placeholder?: string;
  width?: string | number;
}

const categoryOptions = [
  'Issues',
  'Builds',
  'Pull Requests',
  'Commits',
  'Deployments',
];

const SearchBox: React.FC<SearchBoxProps> = ({ placeholder }) => {
  const { t } = useTranslation();
  const finalPlaceholder = placeholder || t('searchbox-placeholder');

  const initialQuery = (() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('q') || '';
    }
    return '';
  })();
  const [query, setQuery] = useState<string>(initialQuery);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const navigate = useNavigate();
  const { teamspaceId } = useTeamspaceContext();
  const { user, isAuthenticated, roles } = useUserAuth();
  const { createNewLoop } = useLoopContext();

  // Use SearchContext
  const {
    selectedCategory,
    setSelectedCategory,
    setSearchQuery,
    triggerSearch,
  } = useSearchContext();

  const prevCreateNewLoopRef = useRef(createNewLoop);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [hasLoops, setHasLoops] = useState(false);
  const [isLoadingLoops, setIsLoadingLoops] = useState(true);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);

  const userRef = useRef(user);
  const rolesRef = useRef(roles);

  useEffect(() => {
    userRef.current = user;
    rolesRef.current = roles;
  }, [user, roles]);

  const loopService = useMemo(
    () =>
      new LoopDataService(
        () => userRef.current,
        () => isAuthenticated,
      ),
    [isAuthenticated],
  );

  const checkForLoops = useCallback(async () => {
    if (!teamspaceId || !isAuthenticated) {
      setHasLoops(false);
      setIsLoadingLoops(false);
      return;
    }

    setIsLoadingLoops(true);
    try {
      const isAdmin = rolesRef.current?.includes('ROLE_ADMIN') || false;

      const response = isAdmin
        ? await loopService.getLoop(teamspaceId)
        : await loopService.getLoopUser(teamspaceId);

      const loops = response?._embedded?.loops || [];
      const loopsExist = loops.length > 0;
      setHasLoops(loopsExist);
    } catch (error) {
      setHasLoops(false);
    } finally {
      setIsLoadingLoops(false);
    }
  }, [isAuthenticated, loopService, teamspaceId]);

  // Automatically fetch loops when teamspace changes; ignore token refreshes
  useEffect(() => {
    if (isAuthenticated) {
      checkForLoops();
    }
  }, [checkForLoops, isAuthenticated]);

  useEffect(() => {
    if (prevCreateNewLoopRef.current === true && createNewLoop === false) {
      const pollForLoops = async () => {
        await checkForLoops();
        if (!hasLoops) {
          const attempts = [500, 1000, 2000, 3000];
          for (const delay of attempts) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            await checkForLoops();
            if (hasLoops) break;
          }
        }
      };

      pollForLoops();
    }

    prevCreateNewLoopRef.current = createNewLoop;
  }, [checkForLoops, createNewLoop, hasLoops]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Minimum characters required to enable search submissions
  const MIN_QUERY_LENGTH = 3;

  // Disable input/category if loading, no teamspace, or no loops
  const isDisabled = isLoadingLoops || !teamspaceId || !hasLoops;

  // Disable search action (button / Enter) while loading OR insufficient length
  const isSearchActionDisabled =
    isLoadingLoops || query.trim().length < MIN_QUERY_LENGTH;

  // Tooltip messaging (show reason why action disabled)
  const tooltipMessage = (() => {
    if (!teamspaceId) return t('tooltip-no-teamspace');
    if (!hasLoops) return t('tooltip-no-loops');
    if (query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH) {
      return t('tooltip-min-chars', { min: MIN_QUERY_LENGTH });
    }
    return '';
  })();

  const handleSearch = () => {
    if (query.trim().length >= MIN_QUERY_LENGTH && !isSearchActionDisabled) {
      setSearchQuery(query);

      // Trigger search flag — SearchResults will handle the actual API call
      triggerSearch();

      // Navigate with both query and category
      navigate(
        `/search?q=${encodeURIComponent(query)}&category=${encodeURIComponent(selectedCategory)}`,
      );
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isSearchActionDisabled) {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <TooltipProvider>
      <div
        ref={dropdownRef}
        id="id-search-box-wrapper"
        data-testid="search-box-wrapper"
      >
        {searchOpen ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            className="flex items-center overflow-hidden rounded bg-white/20"
            id="id-search-form"
            data-testid="search-form"
          >
            {/* Category Dropdown */}
            <div
              className="relative"
              id="id-search-box-category-wrapper"
              data-testid="search-box-category-wrapper"
            >
              <select
                value={selectedCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                disabled={isDisabled}
                className="cursor-pointer appearance-none border-none bg-transparent py-1.5 pl-3 pr-7 text-sm text-white outline-none transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {categoryOptions.map((option) => (
                  <option key={option} value={option} className="bg-blue-600">
                    {option}
                  </option>
                ))}
              </select>
              <KeyboardArrowDown
                sx={{ fontSize: 16 }}
                className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-white"
              />
            </div>

            {/* Divider */}
            <div
              className="h-6 w-px bg-white/30"
              id="id-search-box-divider"
              data-testid="search-box-divider"
            ></div>

            {/* Search Input */}
            <div
              className="flex items-center gap-1 px-2"
              id="id-search-box-input-wrapper"
              data-testid="search-box-input-wrapper"
            >
              <Search sx={{ fontSize: 24 }} className="text-white" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={finalPlaceholder}
                disabled={isDisabled}
                autoComplete="off"
                className="w-48 border-none bg-transparent py-1.5 text-white outline-none placeholder:text-white/60 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={t('aria-search-query')}
                data-testid="search-input"
                id="search-box-input"
              />
              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setQuery('');
                }}
                className="rounded p-1 transition-colors hover:bg-white/20"
                aria-label="Close search"
                id="search-box-close-button"
              >
                <Close sx={{ fontSize: 16 }} className="text-white" />
              </button>
            </div>
          </form>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setSearchOpen(true)}
                className="rounded p-2 transition-colors hover:bg-white/20"
                id="search-box-toggle-button"
                type="button"
              >
                <Search sx={{ fontSize: 24 }} className="text-white" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{tooltipMessage || 'Search'}</TooltipContent>
          </Tooltip>
        )}
        {searchOpen &&
          tooltipMessage &&
          (isDisabled ||
            (query.trim().length > 0 &&
              query.trim().length < MIN_QUERY_LENGTH)) && (
            <Tooltip open>
              <TooltipTrigger asChild>
                <span
                  className="sr-only"
                  id="id-search-box-tooltip-text"
                  data-testid="search-box-tooltip-text"
                >
                  {tooltipMessage}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start">
                {tooltipMessage}
              </TooltipContent>
            </Tooltip>
          )}
      </div>
    </TooltipProvider>
  );

  function setSearchCategory(value: string) {
    setSelectedCategory(value);
  }
};

export default SearchBox;
